import React from 'react'
import { DashboardButton } from './Button'

/**
 * The four things a dashboard surface shows when it has no data to show.
 *
 * These were open-coded per screen, so the same situation looked different
 * depending on where you hit it — some empties were a centred paragraph, some a
 * full-height illustration, one reserved 100vh on a phone. Routing them through
 * here keeps the box identical and only the sentence changing.
 *
 * Errors are rendered inline rather than raised as a toast: neither dashboard
 * mounts a toast host on its own shell, and an error that belongs to one card
 * should stay in that card.
 */

const BOX =
  'rounded-2xl border border-dashboard-hairline bg-white/80 px-6 py-8 text-center text-[13px] font-poppins'

export const EmptyState: React.FC<{ message: string; className?: string }> = ({
  message,
  className = '',
}) => <div className={`${BOX} text-dashboard-muted ${className}`}>{message}</div>

export const ErrorState: React.FC<{
  message: string
  onRetry?: () => void
  className?: string
}> = ({ message, onRetry, className = '' }) => (
  <div className={`${BOX} text-dashboard-negative ${className}`} role="alert">
    {message}
    {onRetry && (
      // The kit's own button, so the one control in an error state matches every
      // other control on the screen. It was hand-rolled here at rounded-lg while
      // the rest of the dashboard moved to pills.
      <DashboardButton variant="secondary" onClick={onRetry} className="mt-3 mx-auto">
        Try again
      </DashboardButton>
    )}
  </div>
)

/**
 * A placeholder sized to the content it stands in for. Pass the real card's
 * height so the layout does not jump when the data lands.
 */
export const LoadingBlock: React.FC<{ height?: string; className?: string }> = ({
  height = 'h-24',
  className = '',
}) => <div className={`${height} rounded-lg bg-gray-100 animate-pulse ${className}`} aria-hidden />

/** Card-shaped skeleton for grids that load a row of cards at once. */
export const SkeletonCard: React.FC<{ height?: string; className?: string }> = ({
  height = 'h-[196px]',
  className = '',
}) => (
  <div
    className={`${height} rounded-xl border border-[#E6ECF1] bg-white animate-pulse ${className}`}
    aria-hidden
  />
)

/** Inline empty row for use inside a table card, where the box would double up. */
export const InlineEmpty: React.FC<{ message: string }> = ({ message }) => (
  <div className="px-6 py-8 text-center text-[13px] text-dashboard-muted font-poppins">
    {message}
  </div>
)
